import { Suspense, PropsWithChildren } from 'react';

import { ErrorBoundary, FallbackProps } from 'react-error-boundary';

import { AnalyticsInitializer } from '@cast/infrastructure-analytics';
import {
  InitAuth,
  InitFingerprint,
  isFailedToAuthorizeError,
  useUser,
} from '@cast/infrastructure-auth';
import {
  InitOrganization,
  OrganizationsProvider,
  useCurrentOrganization,
} from '@cast/infrastructure-organization';
import { InitRouter } from '@cast/infrastructure-navigation';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { SuspenseLoader } from 'components/loaders';
import { useMarkReportingEligibleFromCastAiWebCookie } from 'core/initializer/_hooks/useMarkReportingEligibleFromCastAiWebCookie';
import { useInitSupportChat } from 'hooks/useInitSupportChat';
import { BASE_PATHS } from '@oneclick/constants/navigation';
import { redirectToScreen } from '@oneclick/utils/auth';

const Fallback = ({ error }: FallbackProps) => {
  if (!isFailedToAuthorizeError(error)) {
    throw error;
  }
  redirectToScreen(window.location.href);
  return null;
};

const AppAnalyticsInitializer = () => {
  const user = useUser();
  const currentOrganization = useCurrentOrganization();
  const analyticsDataReady = !!currentOrganization;

  return (
    <AnalyticsInitializer
      appName="console-oneclick"
      enabled={analyticsDataReady}
      email={user.email}
      orgID={currentOrganization?.id ?? ''}
      orgName={currentOrganization?.name ?? ''}
    />
  );
};

const InitSupportChat = () => {
  useInitSupportChat();
  return null;
};

/**
 * Runs after OrganizationsProvider mounts so current org is available for
 * mark-eligible. Blocks children with a loader until eligibility is applied
 * so reporting signup does not briefly see the full Cast AI shell.
 */
const MarkReportingEligibleFromMarketingCookie = ({
  children,
}: PropsWithChildren) => {
  const { isReady } = useOrganizations();
  const isReportingCookieEligibilityPending =
    useMarkReportingEligibleFromCastAiWebCookie(!isReady);

  if (isReportingCookieEligibilityPending) {
    return <SuspenseLoader />;
  }

  return children;
};

type Props = PropsWithChildren<{
  initAuth?: boolean;
  initOrganization?: boolean;
}>;

export const InitApp = ({ children, initAuth, initOrganization }: Props) => {
  return (
    <ErrorBoundary FallbackComponent={Fallback}>
      <Suspense fallback={<SuspenseLoader />}>
        <InitRouter />
        {initAuth ? (
          <>
            <InitAuth />
            <InitFingerprint />
            <InitSupportChat />
          </>
        ) : null}
        {initOrganization ? <InitOrganization /> : null}
        <AppAnalyticsInitializer />
        <OrganizationsProvider organizationSwitchPath={BASE_PATHS.clusters}>
          <MarkReportingEligibleFromMarketingCookie>
            {children}
          </MarkReportingEligibleFromMarketingCookie>
        </OrganizationsProvider>
      </Suspense>
    </ErrorBoundary>
  );
};
